import { jsx as _jsx } from "react/jsx-runtime";
import { createDOMRenderer, FluentProvider, Portal, RendererProvider } from "@fluentui/react-components";
import { useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Logger } from "core/Misc/logger";
import { ToastProvider } from "../primitives/toast";
function ToFeaturesString(options) {
    const { defaultWidth, defaultHeight, defaultLeft, defaultTop } = options;
    const features = [];
    if (defaultWidth !== undefined) {
        features.push({ key: "width", value: defaultWidth.toString() });
    }
    if (defaultHeight !== undefined) {
        features.push({ key: "height", value: defaultHeight.toString() });
    }
    if (defaultLeft !== undefined) {
        features.push({ key: "left", value: defaultLeft.toString() });
    }
    if (defaultTop !== undefined) {
        features.push({ key: "top", value: defaultTop.toString() });
    }
    features.push({ key: "location", value: "no" });
    return features.map((feature) => `${feature.key}=${feature.value}`).join(",");
}
/**
 * Allows displaying a child window that can contain child components.
 * @param props Props for the child window.
 * @returns The child window component.
 */
export const ChildWindow = (props) => {
    const { id, children, onOpenChange, imperativeRef: imperativeRef } = props;
    const [windowState, setWindowState] = useState();
    const [childWindow, setChildWindow] = useState();
    const storageKey = id ? `Babylon/Settings/ChildWindow/${id}/Bounds` : null;
    // This function is just for creating the child window itself. It is a function because
    // it must be called synchronously in response to a user interaction (e.g. button click),
    // otherwise the browser will block it as a scripted popup.
    const createWindow = useCallback((options = {}) => {
        if (storageKey) {
            // If we are persisting window bounds, but the window is already open, just use the existing bounds.
            // Otherwise, try to load bounds from storage.
            if (childWindow) {
                options.defaultLeft = childWindow.screenX;
                options.defaultTop = childWindow.screenY;
                options.defaultWidth = childWindow.innerWidth;
                options.defaultHeight = childWindow.innerHeight;
            }
            else {
                const savedBounds = localStorage.getItem(storageKey);
                if (savedBounds) {
                    try {
                        const bounds = JSON.parse(savedBounds);
                        options.defaultLeft = bounds.left;
                        options.defaultTop = bounds.top;
                        options.defaultWidth = bounds.width;
                        options.defaultHeight = bounds.height;
                    }
                    catch {
                        Logger.Warn(`Could not parse saved bounds for child window with key ${storageKey}`);
                    }
                }
            }
        }
        // Half width by default.
        if (!options.defaultWidth) {
            options.defaultWidth = window.innerWidth * (2 / 3);
        }
        // Half height by default.
        if (!options.defaultHeight) {
            options.defaultHeight = window.innerHeight * (2 / 3);
        }
        // Horizontally centered by default.
        if (!options.defaultLeft) {
            options.defaultLeft = window.screenX + (window.innerWidth - options.defaultWidth) * (2 / 3);
        }
        // Vertically centered by default.
        if (!options.defaultTop) {
            options.defaultTop = window.screenY + (window.innerHeight - options.defaultHeight) * (2 / 3);
        }
        // Try to create the child window (can be null if popups are blocked).
        const newChildWindow = window.open("", "", ToFeaturesString(options));
        if (newChildWindow) {
            // Set the title if provided.
            newChildWindow.document.title = options.title ?? id ?? "";
            // Set the child window state.
            setChildWindow((current) => {
                // But first close any existing child window.
                current?.close();
                return newChildWindow;
            });
        }
    }, [childWindow, storageKey]);
    useImperativeHandle(imperativeRef, () => {
        return {
            open: createWindow,
            close: () => setChildWindow(undefined),
        };
    }, [createWindow]);
    // This side effect runs any time the child window instance changes. It does the rest of the child window
    // setup work, including creating resources and state needed to properly render the content of the child window.
    useEffect(() => {
        const disposeActions = [];
        if (childWindow) {
            const body = childWindow.document.body;
            body.style.width = "100%";
            body.style.height = "100%";
            body.style.margin = "0";
            body.style.padding = "0";
            body.style.display = "flex";
            body.style.overflow = "hidden";
            // Setup the window state, including creating a Fluent/Griffel "renderer" for managing runtime styles/classes in the child window.
            setWindowState({ mountNode: body, renderer: createDOMRenderer(childWindow.document) });
            onOpenChange?.(true);
            // Track the most recently observed window bounds. In some browsers (e.g. Firefox), accessing
            // properties like screenX on a closed window throws, so we cache the last known good values
            // to use as a fallback when the dispose runs after the window has already been closed.
            const getBounds = () => ({
                left: childWindow.screenX,
                top: childWindow.screenY,
                width: childWindow.innerWidth,
                height: childWindow.innerHeight,
            });
            let lastBounds = getBounds();
            // When the child window is closed for any reason, transition back to a closed state.
            const onChildWindowUnload = () => {
                setWindowState(undefined);
                setChildWindow(undefined);
                onOpenChange?.(false);
            };
            childWindow.addEventListener("unload", onChildWindowUnload, { once: true });
            disposeActions.push(() => childWindow.removeEventListener("unload", onChildWindowUnload));
            // Capture bounds before the window is unloaded, while its properties are still safe to read.
            const onChildWindowBeforeUnload = () => {
                lastBounds = getBounds();
            };
            childWindow.addEventListener("beforeunload", onChildWindowBeforeUnload);
            disposeActions.push(() => childWindow.removeEventListener("beforeunload", onChildWindowBeforeUnload));
            // If the main window closes, close any open child windows as well (don't leave them orphaned).
            const onParentWindowUnload = () => {
                childWindow.close();
            };
            window.addEventListener("unload", onParentWindowUnload, { once: true });
            disposeActions.push(() => window.removeEventListener("unload", onParentWindowUnload));
            // On dispose, close the child window.
            disposeActions.push(() => childWindow.close());
            // On dispose, save the window bounds.
            disposeActions.push(() => {
                if (storageKey) {
                    if (!childWindow.closed) {
                        lastBounds = getBounds();
                    }
                    localStorage.setItem(storageKey, JSON.stringify(lastBounds));
                }
            });
        }
        return () => {
            disposeActions.reverse().forEach((dispose) => dispose());
        };
    }, [childWindow]);
    if (!windowState) {
        return null;
    }
    const { mountNode, renderer } = windowState;
    return (_jsx(Portal, { mountNode: mountNode, children: _jsx(RendererProvider, { renderer: renderer, targetDocument: mountNode.ownerDocument, children: _jsx(FluentProvider, { style: {
                    display: "flex",
                    flexGrow: 1,
                    flexDirection: "column",
                    overflow: "hidden",
                }, applyStylesToPortals: false, targetDocument: mountNode.ownerDocument, children: _jsx(ToastProvider, { children: children }) }) }) }));
};
//# sourceMappingURL=childWindow.js.map